import React, { FC, useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import {
  CloseIcon,
  CloudUploadIcon,
  InfoCircle,
  UploadedFileIcon,
  UploadIcon,
} from '../../../../assets/icons/index';
import LoaderIcon from '../../../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { InfoBlade } from '../../../../components/info-blade/index';
import { FormTextarea } from '../../../../components/textarea/index';
import { BackendRoute } from '../../../../config/index';
import { useLocale } from '../../../../hooks/index';
import { ApiService } from '../../../../services/index';
import { EventDocusignLinkedFilesSection } from '../../../events/event-form/components/event-docusign-linked-files';
import { isDocusignFileLink } from '../../../events/event-form/utils/resolve-linked-signature-file-links.util';
import { ChildrenFormProps } from '../plan-form.types';
import { FormSchema, stepFour as stepFourFormSchema } from '../plan-form.validation';

export type StepFourProps = ChildrenFormProps;

export const StepFour: FC<StepFourProps> = ({
  handleCloseModal,
  nextFormStep,
  control,
  setFormData,
  prevFormStep,
  errors: { stepFour: errors },
  formData,
  lockMode,
  clearErrors,
  setError,
  companyId,
  docusignFileLinks = [],
}) => {
  const { stepFour } = useWatch<FormSchema>({ control });
  const { success } = stepFourFormSchema.safeParse(stepFour);
  const { messagesLocale } = useLocale();

  const files = stepFour?.files || [];
  const manualFiles = files.filter(
    (file) => !file.docLink || !isDocusignFileLink(file.docLink, docusignFileLinks),
  );
  const isValid = success && (files.some(({ docLink }) => docLink) || files.length === 0);

  const uploadedFiles = manualFiles.reduce((prev, curr) => (curr.docLink ? prev + 1 : prev), 0);
  const isReachedUploadMaximum = uploadedFiles >= 20;

  const getStepFourFilesData = useCallback(() => formData('stepFour.files') || [], [formData]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        fileRejections.forEach(({ file, errors }) => {
          errors.forEach((e) => {
            if (e.code === 'file-invalid-type') {
              setError('stepFour.files', {
                type: 'manual',
                message: `${messagesLocale['INVALID_FILE_TYPE'].replace('{fileType}', file.name)}`,
              });
            }
          });
        });
        return;
      }

      if (isReachedUploadMaximum) return;
      const existingFiles = getStepFourFilesData();
      const filesToUpload = acceptedFiles.slice(0, 20 - existingFiles.length);

      filesToUpload.forEach(async (file) => {
        const { callback, abort } = ApiService.uploadFile<string>();
        const id = uuidv4();
        const { error, success } = stepFourFormSchema.safeParse({
          files: [{ doc: file, docLink: '', loadProgress: 1, abort, id }],
        });

        if (!success) return setError('stepFour.files', error.issues[0]);

        if (success) clearErrors('stepFour.files');

        const existingFiles = getStepFourFilesData();

        setFormData(
          'stepFour.files',
          [...existingFiles, { doc: file, docLink: '', loadProgress: 1, abort, id }],
          { shouldValidate: true },
        );

        const link = await callback(
          { endpoint: BackendRoute.SHARE_CLASSES, routePath: ['file'] },
          ['file', file],
          {
            onUploadProgress: (n) => {
              const currentFiles = getStepFourFilesData();
              setFormData(
                'stepFour.files',
                currentFiles.map((f) => (f.id === id ? { ...f, loadProgress: n } : f)),
              );
            },
            onAboard: () => {
              const currentFiles = getStepFourFilesData();
              setFormData(
                'stepFour.files',
                currentFiles.filter((f) => f.id !== id),
              );
            },
          },
        );

        if (!link) return;
        const currentFiles = getStepFourFilesData();
        setFormData(
          'stepFour.files',
          currentFiles.map((f) => (f.id === id ? { ...f, loadProgress: 100, docLink: link } : f)),
        );
      });
    },
    [
      clearErrors,
      getStepFourFilesData,
      isReachedUploadMaximum,
      messagesLocale,
      setError,
      setFormData,
    ],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: false,
    multiple: true,
    disabled: isReachedUploadMaximum,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xlsx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls'],
      'text/csv': ['.csv'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
  });

  const handleDeleteFile = useCallback(
    (id: string) => {
      const current = (formData('stepFour.files') || []).find((file) => file.id === id);

      if (current && !current.docLink) current.abort();

      const currentData = (formData('stepFour.files') || []).filter((file) => file.id !== id);
      setFormData('stepFour.files', currentData);
      if (currentData.length === 0) return clearErrors('stepFour.files');
    },
    [clearErrors, formData, setFormData],
  );
  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <span className="text-xs font-[450] text-gray-500">
          <AppFormattedMessage id="DOCUMENTS_UPLOAD" />
        </span>
        <InfoBlade text={messagesLocale['PLAN_DOCUMENT_INFO_BLADE']} />
        <input
          {...getInputProps()}
          accept=".pdf, .doc, .docx, .xlsx, .xls, .csv, .png, .jpeg, .jpg"
          className="hidden"
          multiple
          type="file"
        />
        <label
          {...getRootProps()}
          className={twMerge(
            'relative flex w-full items-center gap-6 rounded-lg border border-dashed border-gray-200 p-6',
            isReachedUploadMaximum ? 'cursor-auto' : 'cursor-pointer',
          )}
        >
          {errors?.files ? <InfoCircle className="size-14" thinIconVariant /> : <CloudUploadIcon />}
          <div className="flex flex-col gap-2">
            <div className="flex">
              {errors?.files ? (
                <span className="text-sm font-[550] text-fireside-600">
                  {errors?.files.message?.toString()}
                </span>
              ) : (
                <p className="text-sm font-medium text-gray-700">
                  <span className="underline">
                    <AppFormattedMessage id="CLICK_TO_UPLOAD" />{' '}
                  </span>
                  <AppFormattedMessage id="OR_DRAG_AND_DROP" />
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1 text-label-sm font-[450] text-gray-500">
              <span>
                <AppFormattedMessage
                  id="SUPPORTED_FORMATS"
                  values={{
                    formats: 'doc/docx, xls/xlsx, csv, jpeg/jpg, png or pdf',
                  }}
                />
              </span>
              <span>
                <AppFormattedMessage id="MAXIMUM_FILE_SIZE" values={{ size: 10 }} />
              </span>
            </div>
            {errors?.files && (
              <span className="flex h-9 w-fit items-center gap-1 rounded border border-gray-100 bg-gray-25 px-3 py-[6px] text-sm font-[450] text-gray-700">
                <UploadIcon />
                <AppFormattedMessage id="UPLOAD_AGAIN" />
              </span>
            )}
          </div>
        </label>
        {(manualFiles?.length || 0) > 0 && (
          <div className="flex flex-col gap-2">
            <span className="pb-2 text-xs font-[450] text-gray-500">
              {uploadedFiles > 1 ? (
                <AppFormattedMessage id="COUNT_FILES_UPLOADED" values={{ count: uploadedFiles }} />
              ) : (
                <AppFormattedMessage id="ONE_FILE_UPLOADED" />
              )}
            </span>
            {manualFiles?.map(({ doc, docLink, id, loadProgress }, i) => (
              <div
                className="flex flex-col overflow-hidden rounded border border-brand-50 bg-brand-25"
                key={`${doc?.name}_${i}`}
              >
                <div className="flex justify-between p-3">
                  <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                    {docLink ? (
                      <UploadedFileIcon className="shrink-0" />
                    ) : (
                      <LoaderIcon className="size-4 shrink-0" />
                    )}
                    <span
                      className="block min-w-0 flex-1 truncate text-xs font-[450] text-gray-700"
                      title={doc?.name}
                    >
                      {doc?.name}
                    </span>
                  </div>
                  <Button
                    className="w-fit shrink-0 p-1 hover:bg-gray-50"
                    onClick={() => handleDeleteFile(id || '')}
                    styleType="NONE"
                    type="button"
                  >
                    <CloseIcon className="size-2" />
                  </Button>
                </div>
                <div
                  className={twMerge(
                    'h-[2px] shrink-0 bg-brand-700 transition-all duration-700',
                    docLink && 'bg-transparent',
                  )}
                  style={{ width: (loadProgress || (docLink && 100)) + '%' }}
                />
              </div>
            ))}
          </div>
        )}
        <EventDocusignLinkedFilesSection companyId={companyId} fileLinks={docusignFileLinks} />
        <FormTextarea
          control={control}
          name="stepFour.additionalNotes"
          placeholder={<AppFormattedMessage id="ADDITIONAL_NOTES_OPTIONAL" />}
          styleVariant="DEFAULT_W_PLACEHOLDER"
          wrapperClassName="bg-gray-50"
        />
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {!lockMode && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
              onClick={prevFormStep}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? (
              <AppFormattedMessage id="UPDATE" />
            ) : !lockMode && (stepFour?.additionalNotes || stepFour?.files?.length || 0 > 0) ? (
              'Review'
            ) : (
              <AppFormattedMessage id="SKIP" />
            )}
          </Button>
        </div>
      </div>
    </>
  );
};
